import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getProfileSectionIcon } from './profileSectionIcons';

const statusColor = {
  generated: '#1976d2',
  reviewed: '#2e7d32',
  empty: '#64748b',
  too_long: '#c2410c',
};

const statusLabel = {
  generated: 'Generated',
  reviewed: 'Reviewed',
  empty: 'Empty',
  too_long: 'Too long',
};

const blankEntry = () => ({
  localId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: null,
  topic: '',
  text: '',
  sources: [],
  contributions: [],
});

const cloneEntries = (entries = []) =>
  entries.map((entry) => ({
    localId: entry.id,
    id: entry.id,
    topic: entry.topic || '',
    text: entry.text || '',
    sources: entry.sources || [],
    contributions: entry.contributions || [],
  }));

const sameEntries = (left, right) =>
  left.length === right.length &&
  left.every(
    (entry, index) => entry.topic === right[index].topic && entry.text === right[index].text && entry.id === right[index].id,
  );

const ProfileCardDrawer = ({
  open,
  card,
  section,
  onClose,
  onSave,
  onReview,
  isSaving = false,
}) => {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const baseline = useMemo(() => cloneEntries(section?.entries || []), [section]);

  useEffect(() => {
    if (open) {
      setEntries(cloneEntries(section?.entries || []));
      setSelected([]);
      setConfirmClose(false);
    }
  }, [open, section]);

  const maxChars = card?.maxContentChars || section?.maxContentChars || 5000;
  const characterCount = entries.reduce((sum, entry) => sum + entry.topic.length + entry.text.length, 0);
  const overLimit = characterCount > maxChars;
  const dirty = !sameEntries(entries, baseline);
  const Icon = getProfileSectionIcon(card?.icon);
  const status = section?.status || 'empty';

  const finishClose = async (save) => {
    if (save) {
      await onSave(entries);
      return;
    }
    if (!dirty && status === 'generated') {
      await onReview();
    }
    onClose();
  };

  const requestClose = () => {
    if (isSaving) return;
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    finishClose(false);
  };

  if (!card) return null;

  return (
    <>
      <Drawer anchor='right' open={open} onClose={requestClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
        <Box display='flex' flexDirection='column' height='100%'>
          <Box px={2.5} py={2} borderBottom={1} borderColor='divider' display='flex' gap={1.5} alignItems='flex-start'>
            <Icon sx={{ color: 'primary.main', mt: 0.25 }} />
            <Box flex={1}>
              <Box display='flex' alignItems='center' gap={1}>
                <Typography variant='h6' fontWeight={700}>
                  {card.name}
                </Typography>
                <Chip
                  label={statusLabel[status] || status}
                  size='small'
                  sx={{ bgcolor: `${statusColor[status]}14`, color: statusColor[status], fontWeight: 700 }}
                />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                Review, edit and save the information used by Lead Agents.
              </Typography>
            </Box>
            <IconButton aria-label='Close' onClick={requestClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box px={2.5} py={2} display='flex' alignItems='center' justifyContent='space-between' gap={1}>
            <Box>
              <Typography variant='subtitle1' fontWeight={700}>
                Key points
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Edit the topics and descriptions below.
              </Typography>
            </Box>
            <Box display='flex' gap={1}>
              <Button
                size='small'
                startIcon={<AddIcon />}
                onClick={() => setEntries((current) => [...current, blankEntry()])}
                sx={{ textTransform: 'none' }}
              >
                Add point
              </Button>
              <Button
                size='small'
                color='inherit'
                disabled={!selected.length}
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setEntries((current) => current.filter((entry) => !selected.includes(entry.localId)));
                  setSelected([]);
                }}
                sx={{ textTransform: 'none' }}
              >
                Delete selected
              </Button>
            </Box>
          </Box>

          <Box flex={1} overflow='auto' px={2.5} pb={2} display='flex' flexDirection='column' gap={1.5}>
            {entries.length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                This card has no points yet. Add one to describe this part of the profile.
              </Typography>
            )}
            {entries.map((entry) => (
              <Box key={entry.localId} border={1} borderColor='divider' borderRadius={2} p={1.5}>
                <Box display='flex' alignItems='flex-start' gap={1}>
                  <Checkbox
                    size='small'
                    checked={selected.includes(entry.localId)}
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked
                          ? [...current, entry.localId]
                          : current.filter((id) => id !== entry.localId),
                      )
                    }
                    inputProps={{ 'aria-label': 'Select point' }}
                  />
                  <Box flex={1}>
                    <TextField
                      label='Topic'
                      value={entry.topic}
                      onChange={(event) =>
                        setEntries((current) =>
                          current.map((item) =>
                            item.localId === entry.localId ? { ...item, topic: event.target.value } : item,
                          ),
                        )
                      }
                      fullWidth
                      size='small'
                      sx={{ mb: 1.25 }}
                    />
                    <TextField
                      label='Description'
                      value={entry.text}
                      onChange={(event) =>
                        setEntries((current) =>
                          current.map((item) =>
                            item.localId === entry.localId ? { ...item, text: event.target.value } : item,
                          ),
                        )
                      }
                      fullWidth
                      multiline
                      minRows={3}
                    />
                    <Box display='flex' flexWrap='wrap' gap={0.75} mt={1.25}>
                      {entry.sources.map((source) => (
                        <React.Fragment key={`${entry.localId}-${source.sourcePartId}`}>
                          <Chip size='small' variant='outlined' label={source.name} />
                          {source.evidence ? <Chip size='small' color='primary' variant='outlined' label={source.evidence} /> : null}
                        </React.Fragment>
                      ))}
                      {entry.contributions.map((item) => (
                        <Chip key={`${entry.localId}-${item.label}`} size='small' label={item.label} sx={{ bgcolor: '#f3e8ff', color: '#6b21a8' }} />
                      ))}
                    </Box>
                  </Box>
                  <IconButton
                    aria-label='Delete point'
                    onClick={() => setEntries((current) => current.filter((item) => item.localId !== entry.localId))}
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            px={2.5}
            py={1.5}
            borderTop={1}
            borderColor='divider'
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <Button onClick={requestClose} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='body2' color={overLimit ? 'error' : 'text.secondary'} fontWeight={overLimit ? 700 : 400}>
                {characterCount.toLocaleString()} / {maxChars.toLocaleString()} characters
              </Typography>
              <Tooltip title='Topic and description count toward this limit. Source names and badges do not.'>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
              <Button
                variant='contained'
                disabled={isSaving || overLimit || !dirty}
                onClick={() => onSave(entries)}
                sx={{ textTransform: 'none' }}
              >
                Save changes
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Save changes?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>This card has unsaved edits.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} sx={{ textTransform: 'none' }}>
            Keep editing
          </Button>
          <Button
            onClick={() => {
              setConfirmClose(false);
              setEntries(baseline);
              if (status === 'generated') onReview();
              onClose();
            }}
            sx={{ textTransform: 'none' }}
          >
            Discard
          </Button>
          <Button
            variant='contained'
            disabled={overLimit}
            onClick={async () => {
              setConfirmClose(false);
              await onSave(entries);
            }}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileCardDrawer;
